import { useEffect, useState, Fragment, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { debounce } from 'lodash'
import { useTranslation } from 'react-i18next'

import { selectActiveBuilding } from '../../../reducers/buildingSelectorReducer'
import RadioField from '../../FormLegacy/FormFieldsV2/RadioField'
import API from '../../../api/api'
import Filter from '../../Filter/Filter'
import { getEmptyRecipentsPlaceholder } from '../../Surveys/helpers'

import {
  getRecipientTypesOptions,
  renderSelectPlaceholder,
  getRecipientsTooltipText,
  pluck,
  userGroups,
} from './helpers'

const RecipientsFilter = ({ isDisabled, messagesType, handleChange, values, prevDataClients }) => {
  const { t } = useTranslation(['surveys', 'organisation'])
  const selectedBuilding = useSelector(selectActiveBuilding)
  const isSelectedBuildingConnectedToPMS = selectedBuilding ? selectedBuilding.PMS : false
  const recipientTypesOptions = getRecipientTypesOptions(t)
  const recipientsTooltipText = getRecipientsTooltipText(t)

  const [recipientType, setRecipientType] = useState(recipientTypesOptions[0].value)
  const [residents, setResidents] = useState({})

  const mounted = useRef(false)

  const emptyRecipentsPlaceholder = getEmptyRecipentsPlaceholder(t, recipientType)

  const prepareRecipientsKey = (type) => {
    switch (type) {
      case 'residents':
        return 'clients'
      case 'groups':
        return 'communityGroups'
      case 'units':
        return 'unitTypes'
      default:
        return 'clientTags'
    }
  }

  const prepareValues = (key, data) => {
    switch (key) {
      case 'clients': {
        const preparedClienst = data[key].map((item) => ({
          id: item.id,
          value: item.id,
          text: item.firstName + ' ' + item.lastName,
        }))
        const preparedUserGroups = data.userGroups.map(({ value, id }) => ({
          id,
          value: userGroups.find((group) => group.id === id),
          text: value,
          isGroup: true,
        }))
        return [...preparedUserGroups, ...preparedClienst]
      }
      case 'unitTypes':
        return data[key].map(({ unitType }) => ({ id: unitType, value: unitType, text: unitType }))
      case 'communityGroups':
        return data[key].map(({ name }) => ({ id: name, value: name, text: name }))
      case 'clientTags':
        return data[key].map(({ name, id }) => ({ id, value: id, text: name }))
      default:
        return data[key]
    }
  }

  const getRecipients = async (search, buildingIds, buildingNumber, floor, yardiPropertyId) => {
    try {
      const data = await API.getAnnouncementRecipients(
        messagesType,
        buildingIds,
        recipientType,
        search,
        buildingNumber,
        floor,
        yardiPropertyId,
      )

      const key = prepareRecipientsKey(recipientType)
      const preparedValues = prepareValues(key, data.data)
      setResidents((prev) => ({ ...prev, [recipientType]: preparedValues }))
    } catch (err) {
      console.error(err)
    }
  }

  const [prevSearchValue, setPrevSearchValue] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const debouncedFetch = debounce(getRecipients, 800)
  const hasBuildings = values.buildings.length
  const buildingIds = pluck(values.buildings)

  const buildingNumber = values.buildingNumber ? values.buildingNumber.id : null
  const floor = values.floor?.length ? pluck(values.floor, 'id') : null
  const yardiPropertyId = values.yardiPropertyId ? values.yardiPropertyId.id : null

  useEffect(() => {
    if (prevSearchValue !== searchValue && hasBuildings) {
      window.addEventListener(
        'keydown',
        debouncedFetch(searchValue, buildingIds, buildingNumber, floor, yardiPropertyId),
      )
    }
    setPrevSearchValue(searchValue)
    return () => {
      window.removeEventListener('keydown', debouncedFetch)
    }
  }, [searchValue])

  useEffect(() => {
    if (hasBuildings) {
      getRecipients('', buildingIds, buildingNumber, floor, yardiPropertyId)
    }
  }, [recipientType, values.buildings?.length, values.floor?.length, buildingNumber])

  const name = prepareRecipientsKey(recipientType)

  const recipientList = useMemo(() => residents[recipientType] || [], [residents, recipientType])
  const hasRecipients = recipientList.length > 0
  const label = recipientType === 'clientTags' ? 'tags' : recipientType

  const options = useMemo(() => {
    return hasRecipients && !searchValue
      ? [{ id: 'all', text: `All ${label}`, selectAll: true }, ...recipientList]
      : recipientList
  }, [recipientList, hasRecipients, searchValue])

  const filteredRecipentTypesOptions = useMemo(() => {
    return recipientTypesOptions.filter((options) => {
      if (!isSelectedBuildingConnectedToPMS && options.value === 'units') {
        return false
      }
      return true
    })
  }, [isSelectedBuildingConnectedToPMS, recipientTypesOptions])

  const handleResetRecipentFields = () => {
    ;[
      'residents',
      'groups',
      'units',
      'clientTags',
      'clients',
      'communityGroups',
      'unitTypes',
    ].forEach((inputName) => {
      handleChange({
        target: {
          name: inputName,
          value: [],
        },
      })
    })
  }

  useEffect(() => {
    if (!prevDataClients || mounted.current) {
      return
    }

    setRecipientType(prevDataClients.type)

    if (prevDataClients.type === 'residents') {
      // Note: Groups are re-selected as groups so they resolve to whoever is in them at sending time,
      // and hand-picked residents come from the details, which leave out those who moved out
      const { userGroupIds, standaloneClientIds, standaloneClients } = prevDataClients
      const residentOptions = residents.residents
      if (
        (userGroupIds.length && !residentOptions) ||
        (standaloneClientIds.length && !standaloneClients)
      ) {
        return
      }

      const selectedGroups = (residentOptions || []).filter(
        (option) => option.isGroup && userGroupIds.includes(option.id),
      )
      const selectedClients = (standaloneClients || []).map(({ id, firstName, lastName }) => ({
        id,
        value: id,
        text: `${firstName} ${lastName}`,
      }))

      handleChange({
        target: {
          name: prepareRecipientsKey('residents'),
          value: [...selectedGroups, ...selectedClients],
        },
      })
      mounted.current = true
      return
    }

    const matchingOptions = options.filter(
      (option) =>
        prevDataClients.optionIds.includes(option.id) ||
        prevDataClients.optionIds.includes(option.text),
    )

    handleChange({
      target: {
        name,
        value: matchingOptions,
      },
    })

    if (matchingOptions.length) {
      mounted.current = true
    }
  }, [prevDataClients, residents, options])

  return (
    <div className="recipient-filter__wrap">
      <div className="recipient-filter__text">{t('surveys:recipientsLabel')}</div>
      <div className="recipient-filter">
        <div className="recipient-filter__radio-group">
          {filteredRecipentTypesOptions.map(({ id, value, label }) => (
            <Fragment key={id}>
              <RadioField
                type="radio"
                name={`${value}-radio`}
                additionalClass="recipient-filter__radio-group-item"
                label={label}
                onChange={(e) => {
                  handleResetRecipentFields()
                  setRecipientType(e.target.value)
                }}
                checked={recipientType === value}
                value={value}
                data-tip
                data-for={`tooltip-${value}`}
              />
              <ReactTooltip className="tooltip" id={`tooltip-${value}`} multiline effect="solid">
                {recipientsTooltipText[value]}
              </ReactTooltip>
            </Fragment>
          ))}
        </div>

        <Filter
          name={name}
          placeholder={renderSelectPlaceholder(values[name], emptyRecipentsPlaceholder, t)}
          additionalClass="recipient-filter__input-wrap"
          options={options}
          handleSearch={(val) => setSearchValue(val)}
          searchValue={searchValue}
          value={values[name]}
          onChange={handleChange}
          config={{
            multiple: true,
            hasSearch: recipientType === 'residents',
          }}
          disabled={!options.length || isDisabled}
        />
      </div>
    </div>
  )
}

export default RecipientsFilter

RecipientsFilter.propTypes = {
  isDisabled: PropTypes.bool,
  messagesType: PropTypes.string,
  handleChange: PropTypes.func,
  values: PropTypes.object,
  prevDataClients: PropTypes.object,
}
